<template>
    <ResponsiveDialog
        :value="value"
        @input="$emit('input', $event)"
        :max-width="420"
        :full-height="false"
        :bottom-inset="54"
        :header-title="$t('journey.cityHeal.title')"
        header-class="skin-window-header--pokecenter"
        card-class="skin-card"
    >
        <div class="heal-dialog-bg skin-window--pokecenter">
            <div class="heal-dialog-banner" :style="bannerStyle"/>
            <div v-if="isLoading" class="heal-dialog-banner-caption">{{ $t('journey.cityHeal.healedCaption') }}</div>
            <div class="heal-dialog-content">
                <div v-if="injuredPokemons.length > 0" class="heal-dialog-injured-row">
                    <v-avatar
                        v-for="p in injuredPokemons"
                        :key="p.id"
                        size="56"
                        class="heal-dialog-injured-avatar"
                        :style="{borderColor: hpColor(p)}"
                    >
                        <v-img :src="pokemonIcon(p)" cover/>
                    </v-avatar>
                </div>
                <div
                    class="heal-dialog-cost-pill"
                    :class="{'heal-dialog-cost-pill--loading': isLoading || !pokemonsLoaded, 'heal-dialog-cost-pill--disabled': noneInjured}"
                    @click="pokemonsLoaded && !isLoading && !noneInjured && heal()"
                >
                    <v-progress-circular v-if="isLoading || !pokemonsLoaded" indeterminate size="22" width="3" color="white"/>
                    <template v-else-if="noneInjured">
                        {{ $t('journey.cityHeal.noneInjured') }}
                    </template>
                    <template v-else>
                        {{ $t('journey.cityHeal.healCostPrefix') }}
                        <span class="heal-dialog-cost-value">
                            {{ stepCost }} <v-icon color="white" size="30" class="ml-1">mdi-shoe-print</v-icon>
                        </span>
                    </template>
                </div>
            </div>
        </div>
    </ResponsiveDialog>
</template>

<script>
    // Heal popup - klik na Pokemon Center otevře popup se sestrou Joy a
    // tlačítkem "Heal". Cenu kroku vlastní rodič (CityNext.vue). Design
    // viz claude/components/journey-city.md.
    //
    // Řádek zraněných pokémonů čte VŠECHNY pokémony vlastním fetch
    // (pokemonApi.pokemonListAll), ne sdílený `pokemon/items` getter -
    // ten plní jen AKTIVNÍ tým (TeamBottomSheet.vue, inProfesorOak=false),
    // ale backend `poke-center` léčí i pokémony odložené u Oaka.
    import {mapActions} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import * as pokemonApi from '@/api/pokemonApi';
    import {pokemonImageSrc} from '@/utils/pokemonImage';

    export default {
        name: 'CityHealDialog',
        components: {ResponsiveDialog},
        props: {
            value: {type: Boolean, default: false},
            trainerId: {type: Number, required: true},
            stepCost: {type: Number, default: 2},
            isLoading: {type: Boolean, default: false},
        },
        data() {
            return {
                allPokemons: [],
                pokemonsLoaded: false,
                gifFrozen: false,
            }
        },
        computed: {
            injuredPokemons() {
                return this.allPokemons.filter((p) => parseInt(p.hp) < parseInt(p.maxHp));
            },
            // `pokemonsLoaded` brání krátkému bliknutí "žádný zraněný" stavu
            // dřív, než dorazí fetch po otevření popupu.
            noneInjured() {
                return this.pokemonsLoaded && this.injuredPokemons.length === 0;
            },
            // Prázdný objekt mimo isLoading = CSS třída .heal-dialog-banner si
            // drží svůj výchozí background-image (nurse-joy.png), inline styl
            // ho přebije jen kdyz je co měnit (léčení -> Chansey GIF).
            bannerStyle() {
                if (!this.isLoading) {
                    return {};
                }
                const img = this.gifFrozen
                    ? require('@/assets/pic/web/nurse-joy-heal-frozen.png')
                    : require('@/assets/pic/web/nurse-joy-heal.gif');
                return {backgroundImage: `url(${img})`};
            },
        },
        watch: {
            value: {
                immediate: true,
                handler(isOpen) {
                    if (isOpen) {
                        this.loadAllPokemons();
                    }
                },
            },
            // GIF má přesně 1200ms/12 snímků po 100ms - přepnutí na statický
            // snímek o pár ms dřív (1100ms) nechá GIF poslední snímek doběhnout,
            // ale nestihne ho přetočit zpátky na začátek.
            isLoading(loading) {
                if (loading) {
                    this.gifFrozen = false;
                    setTimeout(() => {
                        this.gifFrozen = true;
                    }, 1100);
                }
            },
        },
        methods: {
            ...mapActions({
                fetchPokemon: 'pokemon/fetchObject',
            }),
            loadAllPokemons() {
                this.pokemonsLoaded = false;
                this.fetchPokemon(pokemonApi.pokemonListAll(this.trainerId)).then((data) => {
                    this.allPokemons = (data && data.member) || [];
                    this.pokemonsLoaded = true;
                });
            },
            heal() {
                this.$emit('heal');
            },
            pokemonIcon(p) {
                return pokemonImageSrc(p.pokemonType.img);
            },
            hpPercent(p) {
                return parseInt(p.hp) / parseInt(p.maxHp) * 100;
            },
            hpColor(p) {
                const pct = this.hpPercent(p);
                if (pct <= 25) return '#E53935';
                if (pct <= 60) return '#FB8C00';
                return '#43A047';
            },
        },
    }
</script>

<style scoped>
.heal-dialog-bg {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
/* pozadí řídí skin (public/skins/normal.css, .skin-window--pokecenter). */
.heal-dialog-banner {
    flex: none;
    /* ruší flex stretch (cross-axis) - bez tohodle by "width: auto" z
       aspect-ratio níže neměl žádný efekt. */
    align-self: center;
    height: 160px;
    /* skutečný poměr stran nurse-joy.png (592x438) - banner má tak přesně
       šířku fotky, žádná prázdná matová plocha uvnitř rámečku. */
    aspect-ratio: 592 / 438;
    margin: 16px auto 0;
    border-radius: 16px;
    background-image: url('@/assets/pic/web/travel/nurse-joy.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    box-shadow: 0 6px 14px rgba(0,0,0,0.3), 0 2px 5px rgba(0,0,0,0.2);
    background-color: inherit;
}
.heal-dialog-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 28px;
    /* extra místo dole - placeholder pro plovoucí oranžový křížek
       (ResponsiveDialog, fab vpravo dole), stejná hodnota jako
       CityTravelDialog.vue/OfficerJennyDialog.vue/TrainerList.vue. */
    padding: 28px 20px var(--responsive-dialog-close-clearance) 20px;
}
.heal-dialog-injured-row {
    /* mřížka místo flex-wrap - pevný počet sloupců (3 mobil, 6 široký) */
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-items: center;
    gap: 12px;
    width: 100%;
}
@media (min-width: 600px) {
    .heal-dialog-injured-row {
        grid-template-columns: repeat(6, 1fr);
    }
}
.heal-dialog-injured-avatar {
    background: #fff;
    /* barva lemu se dosazuje inline (:style, viz hpColor v methods) */
    border: 3px solid;
    box-shadow: 1px 2px 5px rgba(0,0,0,0.3);
}
.heal-dialog-cost-pill {
    /* celá stínovaná pilulka je klikací "Heal" tlačítko, ne jen popisek
       vedle samostatného tlačítka - stejný mobilní vzor jako NextCircleTile.vue */
    background: #F06292;
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    padding: 14px 26px;
    border-radius: 999px;
    text-align: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    min-width: 55%;
    min-height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
        0 6px 14px rgba(216,27,96,0.35),
        1px 1px 4px 3px rgba(0,0,0,0.5),
        -1px -2px 6px 3px rgba(0,0,0,0.65) inset;
}
.heal-dialog-cost-pill:active {
    box-shadow: 1px 1px 3px 2px rgba(0,0,0,0.7) inset;
}
.heal-dialog-cost-pill--loading {
    pointer-events: none;
    opacity: 0.85;
}
.heal-dialog-cost-pill--disabled {
    pointer-events: none;
    background: #BDBDBD;
    box-shadow: 1px 1px 4px 3px rgba(0,0,0,0.25), -1px -2px 6px 3px rgba(0,0,0,0.35) inset;
}
.heal-dialog-cost-value {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}
.heal-dialog-banner-caption {
    margin-top: 8px;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
}
</style>
